/**
 * The Lushin Discovery Call Playbook, as served by GET /api/playbook.
 *
 * Mirrors backend/src/models/DiscoveryPlaybook.js. The live checklist and the
 * post-call rubric are the same taxonomy seen from opposite ends: `rubricAspect`
 * is the join key, which is why it is read-only here — renaming it produces live
 * cards that can never be scored.
 */

/** How an item is satisfied. Drives detection routing and UI treatment. */
export type PlaybookItemBehavior = 'do' | 'respond' | 'avoid' | 'metric';

export type GuardrailSeverity = 'warn' | 'critical';

export interface PlaybookExample {
  speaker: 'salesperson' | 'customer';
  text: string;
  why: string;
}

/** RESPOND items: what the CUSTOMER says that opens the opportunity. */
export interface PlaybookTrigger {
  cue: string;
  windowSeconds?: number;
  windowTurns?: number;
}

/** What breaches an item, and how the rep recovers from it. */
export interface PlaybookViolation {
  cue: string;
  severity: GuardrailSeverity;
  /** The cost, in the rubric's coaching voice. Mechanism, never judgement. */
  why: string;
  /** What to say right now to get back on track. */
  recovery?: string;
  /**
   * Item keys that, once satisfied, silence this guardrail entirely. Read-only:
   * it encodes when a violation stops being one ("pitching after pain AND
   * budget are qualified is correct"), which is a rubric statement.
   */
  suppressWhen?: string[];
}

export interface PlaybookMetric {
  kind: 'talk_ratio';
  threshold?: number;
  byMinute?: number;
}

export interface PlaybookItem {
  key: string;
  label: string;
  order: number;
  rubricAspect: string;
  behavior: PlaybookItemBehavior;
  intent: string;
  exemplars?: string[];
  followUps?: string[];
  trigger?: PlaybookTrigger;
  violation?: PlaybookViolation;
  metric?: PlaybookMetric;
  prerequisites?: string[];
  optional?: boolean;
  phaseHint?: 'open' | 'body' | 'close' | 'always';
  subConditions?: string[];
  examples?: {
    positive?: PlaybookExample[];
    negative?: PlaybookExample[];
  };
}

export interface PlaybookCategory {
  key: string;
  number: number;
  title: string;
  /** 1 is the "at any cost" set — the same four keys the weight editor protects. */
  tier: 1 | 2 | 3;
  phaseHint?: 'open' | 'body' | 'close' | 'always';
  items: PlaybookItem[];
}

/** Detection thresholds, versioned with the content so they tune per tenant. */
export interface PlaybookDetection {
  guardrailMinConfidence: number;
  hitMinConfidence: number;
  likelyMinConfidence: number;
  guardrailWarnCooldownSeconds: number;
  guardrailSoundEnabled: boolean;
  guardrailWarnAutoDismissSeconds: number;
}

export interface DiscoveryPlaybook {
  playbookId: string;
  version: number;
  rubricId: string;
  isActive: boolean;
  detection: PlaybookDetection;
  categories: PlaybookCategory[];
  updatedBy?: string;
  updatedByName?: string;
  updatedAt?: string;
}

/** Human labels for the four item behaviours, used in the editor's badges. */
export const BEHAVIOR_LABELS: Record<PlaybookItemBehavior, string> = {
  do: 'Do',
  respond: 'Respond',
  avoid: 'Avoid',
  metric: 'Measured'
};

/**
 * What each behaviour means, shown as a tooltip. Worth saying in the UI: the
 * split is not cosmetic — it decides which speaker's turn can satisfy an item,
 * and therefore what the rep sees.
 */
export const BEHAVIOR_DESCRIPTIONS: Record<PlaybookItemBehavior, string> = {
  do: 'The rep makes this move. Satisfied by a salesperson turn; fills the checklist.',
  respond: 'The prospect opens a door. Detected on the customer channel, then watched for the rep taking it.',
  avoid: 'A mistake to catch. Fires a guardrail overlay rather than a checklist tick.',
  metric: 'Computed from speaker statistics. Never sent to a model.'
};
